import { PricingQuoteIncludedProductDto } from '@inosio/payments-sdk';
import { Card, Typography, Divider, Button } from '@mui/material';
import { Box } from '@mui/system';
import NumberFormat from 'react-number-format';

export interface PricingQuoteSummaryCardProps {
  /**
   * A list of products included in this package. Comes from a created pricing quote.
   */
  includedProducts: PricingQuoteIncludedProductDto[];

  /**
   * The discount on this package. Comes from a created pricing quote.
   */
  packageDiscount?: number;

  /**
   * The text to show in the button
   */
  buttonText: string;

  /**
   * Callback called when the button is clicked
   */
  onButtonClick: () => void;
}
const PricingQuoteSummaryCard = (props: PricingQuoteSummaryCardProps) => {

  const summaryCardNotes = [
    'Billed monthly in arrears to a bank account of your choice.',
    'Cancel anytime.',
    'New features and services that are added to your account in the future can affect your pricing.',
  ];

  if (!!props.packageDiscount && props.packageDiscount > 0) {
    summaryCardNotes.push('* Discount requires a minimum of 35% rent payment utilization and 90% new tenants are added via Inosio applications and screening.');
  }

  const summaryCardAgreementText = 'By clicking below you agree to Inosio’s Terms of Service and Privacy Policy.';

  const calculateTotalMonthlyCost = (includedProducts: PricingQuoteIncludedProductDto[], packageDiscount?: number) => {
    //get all products which have non zero FlatFee
    const monthlyBilledProducts = includedProducts.filter((prod) => !!prod.FlatFee);

    const monthlyCost = monthlyBilledProducts.map((prod) => (prod.FlatFee || 0) / 100).reduce((prev, next) => prev + next, 0);

    const totalMonthlyCost = monthlyCost - (props.packageDiscount || 0) / 100;

    return totalMonthlyCost;
  };
  return (
    <Card
      sx={{
        padding: 3,
        borderRadius: '15px',
        border: '1px solid #E0E0E0', // Grey border
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: '35px', fontWeight: 'bold' }}>
        <NumberFormat
          thousandsGroupStyle="thousand"
          value={calculateTotalMonthlyCost(props.includedProducts, props.packageDiscount)}
          prefix="$"
          decimalSeparator="."
          displayType="text"
          suffix="/month"
          type="text"
          thousandSeparator={true}
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </Typography>
      <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: '18px', textAlign: 'center', mt: 1 }}>+ Consumables</Typography>
      <Divider sx={{ my: 2, width: '100%' }} />
      <Box sx={{ textAlign: 'left', width: '80%' }}>
        {summaryCardNotes.map((note, index) => (
          <Typography key={index} sx={{ fontFamily: 'Work Sans !important', fontSize: '14px', mb: 1 }}>
            • {note}
          </Typography>
        ))}
      </Box>
      <Typography sx={{ fontFamily: 'Work Sans !important', fontSize: '12px', color: 'gray', textAlign: 'center', mt: 2 }}>{summaryCardAgreementText}</Typography>
      <Button
        variant="outlined"
        sx={{
          mt: 2,
          color: '#39A2DB',
          border: '2px solid #39A2DB',
          boxShadow: 'none',
          borderRadius: '24px',
          fontFamily: "'Open Sans', sans-serif",
          fontSize: '12px',
          fontWeight: 700,
          textTransform: 'capitalize',
          '&:hover': {
            backgroundColor: '#39A2DB',
            color: 'white',
            border: '2px solid #39A2DB',
          },
          width: '70%',
          height: '45px',
        }}
        onClick={props.onButtonClick}
      >
        {props.buttonText}
      </Button>
    </Card>
  );
};

export default PricingQuoteSummaryCard;
